import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  DataResponse,
  KomponentInsert,
  KomponentInsertResult,
  KomponentKusovnik,
  KomponentMeta,
} from "@/api/schemas";

/** Zoznam parametrických komponentov knižnice (most Konštrukcia → Rozpočet). */
export function useKomponenty(): UseQueryResult<DataResponse<KomponentMeta[]>> {
  return useQuery({
    queryKey: queryKeys.komponenty.list(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<KomponentMeta[]>>(
        "/komponenty",
      );
      return data;
    },
    staleTime: 5 * 60 * 1000, // knižnica sa mení len deployom
  });
}

/** Dry-run kusovník komponentu pre zadané parametre (náhľad pred vložením). */
export function useKomponentKusovnik(): UseMutationResult<
  DataResponse<KomponentKusovnik>,
  Error,
  {
    komponentId: string;
    params: Record<string, unknown>;
    /** Korpusový materiál (doskové dielce) — null = auto podľa tokenu. */
    materialId?: string | null;
  }
> {
  return useMutation({
    mutationFn: async ({ komponentId, params, materialId }) => {
      const { data } = await api.post<DataResponse<KomponentKusovnik>>(
        `/komponenty/${encodeURIComponent(komponentId)}/kusovnik`,
        { params, material_id: materialId ?? null },
      );
      return data;
    },
  });
}

/** Vloží komponent do varianty ako novú zostavu s dielcami a kovaním. */
export function useVlozKomponent(
  zakazkaId: string,
  variantaId: string,
): UseMutationResult<
  DataResponse<KomponentInsertResult>,
  Error,
  KomponentInsert
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: KomponentInsert) => {
      const { data } = await api.post<DataResponse<KomponentInsertResult>>(
        `/varianty/${variantaId}/zostavy/z-komponentu`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: queryKeys.zakazky.detail(zakazkaId),
      });
    },
  });
}
